import moment, { Moment } from 'moment/moment'

/** SSO-3550: club branding palette, keys mirror the API / DB column names. */
export interface ClubColors {
    color_bg_top: string
    color_bg_bottom: string
    color_surface: string
    color_border: string
    color_accent: string
    color_accent_light: string
    color_text_muted: string
}

export const CLUB_COLOR_FIELDS: (keyof ClubColors)[] = [
    'color_bg_top',
    'color_bg_bottom',
    'color_surface',
    'color_border',
    'color_accent',
    'color_accent_light',
    'color_text_muted',
]

const EMPTY_COLORS: ClubColors = <ClubColors>CLUB_COLOR_FIELDS
    .reduce((acc: any, field) => ({ ...acc, [field]: '' }), {})

export interface ClubSectionElementPayload {
    id?: number
    element_type: string
    element_id?: number
    sort_order?: number
    is_premium?: boolean
    element?: any
}

export interface ClubSectionPayload {
    id?: number
    name: string
    sort_order?: number
    elements?: ClubSectionElementPayload[]
    translations?: {}
}

export interface ClubPayload {
    id: number
    name: string
    description: string
    photo_url: string
    photos: string[]
    landing_photos: string[]
    metadata?: {}
    country: string
    city: string
    club_type: string
    color_bg_top: string
    color_bg_bottom: string
    color_surface: string
    color_border: string
    color_accent: string
    color_accent_light: string
    color_text_muted: string
    sort_order: number
    followers_count: number
    is_follower?: boolean
    enabled?: boolean
    created_at: string
    sections?: ClubSectionPayload[]
    translations?: {}
}

/**
 * A single element placement within a section — dispatches to whichever
 * entity type (training_program / challenge / event / athlete / product /
 * content) is referenced. `itemId` is the target entity's own id, not the
 * pivot row's id (kept separately as `id` only for :key/debugging).
 */
export class ClubSectionElement {

    id?: number
    type: string
    itemId?: number
    name: string
    isPremium: boolean

    constructor(data: ClubSectionElementPayload = <any>{}) {
        this.id = data.id
        this.type = data.element_type
        this.itemId = data.element_id || data.element?.id
        this.name = data.element ? (data.element.name || '') : ''
        this.isPremium = !! data.is_premium
    }

    payload(): any {
        return {
            type: this.type,
            id: this.itemId,
            is_premium: this.isPremium,
        }
    }
}

export class ClubSection {

    id?: number
    name: string
    translations?: {} = <any>{}
    elements: ClubSectionElement[] = []

    // UI-only draft state for the "add element" row — not sent in payload().
    newElementType: string = ''
    newElementId?: number = undefined
    newElementPremium: boolean = false

    constructor(data: ClubSectionPayload = <any>{}) {
        this.id = data.id
        this.name = data.name
        this.translations = data.translations || {}
        this.elements = (data.elements || []).map((item) => new ClubSectionElement(item))
    }

    payload(): any {
        return {
            id: this.id,
            name: this.name,
            translations: this.translations,
            elements: this.elements.map((item) => item.payload()),
        }
    }

    addElement(type: string, itemId: number, name: string, isPremium: boolean = false): this {
        const element = new ClubSectionElement()
        element.type = type
        element.itemId = itemId
        element.name = name
        element.isPremium = isPremium
        this.elements.push(element)
        return this
    }

    removeElement(index: number): this {
        this.elements.splice(index, 1)
        return this
    }

    moveElement(index: number, direction: -1 | 1): this {
        const target = index + direction
        if (target < 0 || target >= this.elements.length) {
            return this
        }
        const [item] = this.elements.splice(index, 1)
        this.elements.splice(target, 0, item)
        return this
    }
}

export class Club {

    id: number
    name: string
    description: string
    photoUrl: string
    photo?: File
    photos: string[] = []
    photoUps?: File[]
    /** SSO-3532: full-screen artwork for the app's partner onboarding screen, ordered. */
    landingPhotos: string[] = []
    landingUps?: File[]
    metadata?: {} = <any>{}
    country: string
    city: string
    /** SSO-3550: status badge above the club name — '' / null means no badge at all. */
    clubType: string
    /**
     * SSO-3550: the seven branding colours, kept under their API names so the whole block
     * round-trips without a per-field mapping. '' is sent for a cleared picker and the
     * backend stores NULL, which is the app's "derive this one yourself" signal.
     */
    colors: ClubColors = { ...EMPTY_COLORS }
    sortOrder: number
    followersCount: number
    isFollower: boolean
    enabled: boolean
    createdAt: Moment
    sections: ClubSection[] = []
    translations?: {} = <any>{}

    constructor(data: ClubPayload) {
        this.id = data.id
        this.name = data.name
        this.description = data.description
        this.photoUrl = data.photo_url
        this.photos = data.photos || []
        this.landingPhotos = data.landing_photos || []
        this.country = data.country
        this.city = data.city
        this.clubType = data.club_type || ''
        this.colors = Club.readColors(data)
        this.sortOrder = data.sort_order
        this.followersCount = data.followers_count
        this.isFollower = !! data.is_follower
        this.enabled = !! data.enabled
        this.createdAt = moment(data.created_at)
        this.capture(data)
    }

    fill(data: ClubPayload): this {
        this.id = data.id
        this.name = data.name
        this.description = data.description
        this.photoUrl = data.photo_url
        this.photos = data.photos || []
        this.landingPhotos = data.landing_photos || []
        this.country = data.country
        this.city = data.city
        this.clubType = data.club_type || ''
        this.colors = Club.readColors(data)
        this.sortOrder = data.sort_order
        this.followersCount = data.followers_count
        this.isFollower = !! data.is_follower
        this.enabled = !! data.enabled
        this.createdAt = moment(data.created_at)
        this.capture(data)
        return this
    }

    capture(data: ClubPayload): this {
        if (data.metadata) {
            this.metadata = data.metadata
        }
        if (data.sections) {
            this.sections = data.sections.map((item) => new ClubSection(item))
        }
        if (data.translations) {
            this.translations = data.translations
        }
        return this
    }

    delPhoto(number: number): this {
        this.photos = Club.removeAt(this.photos, number)
        return this
    }

    updatePhotoNumber(currentNumber: number, number: number): this {
        this.photos = Club.moveTo(this.photos, currentNumber, number)
        return this
    }

    delLandingPhoto(number: number): this {
        this.landingPhotos = Club.removeAt(this.landingPhotos, number)
        return this
    }

    updateLandingPhotoNumber(currentNumber: number, number: number): this {
        this.landingPhotos = Club.moveTo(this.landingPhotos, currentNumber, number)
        return this
    }

    /**
     * Both photo collections (header + landing) reorder/delete the same way, so the list
     * surgery lives here once. Returns a fresh array — the caller reassigns it, which is
     * what Vue 2 reacts to (index writes on the original would not be picked up).
     */
    private static removeAt(items: string[], index: number): string[] {
        const next = items.map(i => JSON.parse(JSON.stringify(i)))
        next.splice(index, 1)
        return next
    }

    private static moveTo(items: string[], from: number, to: number): string[] {
        const next = items.map(i => JSON.parse(JSON.stringify(i)))
        const [item] = next.splice(from, 1)
        next.splice(to, 0, item)
        return next
    }

    addSection(): this {
        this.sections.push(new ClubSection())
        return this
    }

    removeSection(index: number): this {
        this.sections.splice(index, 1)
        return this
    }

    moveSection(index: number, direction: -1 | 1): this {
        const target = index + direction
        if (target < 0 || target >= this.sections.length) {
            return this
        }
        const [item] = this.sections.splice(index, 1)
        this.sections.splice(target, 0, item)
        return this
    }

    /**
     * Colours arrive flat and any of them may be null; normalise to '' so the pickers
     * bind to a string and a cleared field survives the round-trip as ''  → NULL.
     */
    private static readColors(data: ClubPayload): ClubColors {
        return <ClubColors>CLUB_COLOR_FIELDS
            .reduce((acc: any, field) => ({ ...acc, [field]: (<any>data)[field] || '' }), {})
    }

    exists(): boolean {
        return !! this.id
    }

    unset(): void {
        this.fill(<any>{})
        this.photo = undefined
        this.photoUps = undefined
        this.photos = []
        this.landingUps = undefined
        this.landingPhotos = []
        this.metadata = {}
        this.colors = { ...EMPTY_COLORS }
        this.sections = []
        this.translations = {}
    }

    payload(): ClubPayload | any {
        return {
            id: this.id,
            name: this.name,
            description: this.description,
            photos: this.photos,
            landing_photos: this.landingPhotos,
            metadata: this.metadata,
            country: this.country,
            city: this.city,
            club_type: this.clubType,
            ...this.colors,
            sort_order: this.sortOrder,
            enabled: this.enabled,
            sections: this.sections.map((item) => item.payload()),
            translations: this.translations,
        }
    }
}
